import * as THREE from 'three';

// Blaze Fire skin color palette - INTENSE burning crimson and magma
export const FIRE_COLORS = {
  // Core block colors - deep burning red, not orange
  primary: '#DC143C',        // Crimson red - the main block color
  secondary: '#FF2400',      // Scarlet red (bright highlights)
  emissive: '#8B0000',       // Dark red glow (deep ember)

  // Environment
  background: '#0a0000',     // Near-black with red tint
  gridLine: '#2a0a0a',       // Dark blood red grid

  // Border
  border: '#B22222',         // Firebrick red
  borderGlow: '#DC143C',     // Crimson glow

  // Effect colors - full fire spectrum
  ember: '#FF4500',          // Orange-red for embers
  flame: '#FF6600',          // Orange flames
  flameCore: '#FFFF00',      // Yellow-white flame core (hottest)
  spark: '#FFD700',          // Golden sparks
  lava: '#FF4500',           // Lava orange
  magma: '#8B0000',          // Deep magma red
  smoke: '#2F1810',          // Dark smoke
  ash: '#4a3a3a',            // Ash gray-red

  // Special effects
  coreWhite: '#FFFAF0',      // White-hot core
  heatDistort: '#FF6B35',    // Heat distortion color
};

// Unified crimson palette - all blocks share the same intense burning look
const FIRE_EMBER_PRIMARY = '#DC143C';    // Crimson red
const FIRE_EMBER_EMISSIVE = '#8B0000';   // Dark red glow

export const FIRE_TETROMINO_COLORS = {
  'l-tetromino-active': FIRE_EMBER_PRIMARY,
  'j-tetromino-active': FIRE_EMBER_PRIMARY,
  't-tetromino-active': FIRE_EMBER_PRIMARY,
  'square-tetromino-active': FIRE_EMBER_PRIMARY,
  'line-tetromino-active': FIRE_EMBER_PRIMARY,
  's-tetromino-active': FIRE_EMBER_PRIMARY,
  'z-tetromino-active': FIRE_EMBER_PRIMARY,
};

// Unified emissive glow for all blocks
export const FIRE_TETROMINO_EMISSIVE = {
  'l-tetromino-active': FIRE_EMBER_EMISSIVE,
  'j-tetromino-active': FIRE_EMBER_EMISSIVE,
  't-tetromino-active': FIRE_EMBER_EMISSIVE,
  'square-tetromino-active': FIRE_EMBER_EMISSIVE,
  'line-tetromino-active': FIRE_EMBER_EMISSIVE,
  's-tetromino-active': FIRE_EMBER_EMISSIVE,
  'z-tetromino-active': FIRE_EMBER_EMISSIVE,
};

// Get block color based on tetromino type
export const getBlockColor = (colorClass) => {
  return FIRE_TETROMINO_COLORS[colorClass] || FIRE_COLORS.primary;
};

// Get emissive color for glow
export const getEmissiveColor = (colorClass) => {
  return FIRE_TETROMINO_EMISSIVE[colorClass] || FIRE_COLORS.emissive;
};

// Create fire-styled material for blocks - molten, burning look
export const createFireMaterial = (options = {}) => {
  const {
    color = FIRE_COLORS.primary,
    emissive = FIRE_COLORS.emissive,
    emissiveIntensity = 1.2,
    metalness = 0.3,        // Lower metalness for molten look
    roughness = 0.4,        // Rougher for burning texture
    opacity = 1.0,
    transparent = false,
  } = options;

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    emissive: new THREE.Color(emissive),
    emissiveIntensity,
    metalness,
    roughness,
    opacity,
    transparent,
  });
};

// Create molten lava material for border
export const createMoltenGlassMaterial = () => {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(FIRE_COLORS.border),
    emissive: new THREE.Color(FIRE_COLORS.borderGlow),
    emissiveIntensity: 0.6,
    metalness: 0.4,
    roughness: 0.35,
    opacity: 0.95,
    transparent: true,
  });
};

// Create ghost (hard drop preview) material with fire effect
export const createGhostMaterial = (baseColor) => {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(baseColor),
    emissive: new THREE.Color(FIRE_COLORS.ember),
    emissiveIntensity: 0.6,
    metalness: 0.3,
    roughness: 0.4,
    opacity: 0.4,
    transparent: true,
  });
};

// Create edge highlight material - burning cracks
export const createEdgeMaterial = () => {
  return new THREE.LineBasicMaterial({
    color: new THREE.Color(FIRE_COLORS.ember),
    linewidth: 2,
    opacity: 0.8,
    transparent: true,
  });
};

// Theme configuration
export const THEME_CONFIG = {
  name: 'Blaze',
  background: '#0a0000',      // Near-black with red tint
  bloomIntensity: 1.4,        // Cranked for intense fire glow
  bloomThreshold: 0.05,       // Very low — everything glows
  vignetteOffset: 0.3,
  vignetteDarkness: 0.65,
};
